import { Toggle } from '@convergence/components';
import { useLogsQuery } from '../api/queries/logs';
import { useMemo, useState } from 'react';
import { AnalysisLogsDtoType } from '@convergence/client';
import { Download } from '@phosphor-icons/react';

export interface AnalysisLogsProps {
  slug: string;
}

export function AnalysisLogs(props: AnalysisLogsProps) {
  const logsQuery = useLogsQuery(props.slug);
  console.log(logsQuery.data);

  if (logsQuery.isError) {
    return (
      <div>
        Error loading logs:{' '}
        {logsQuery.error instanceof Error
          ? logsQuery.error.message
          : 'Unknown error'}
      </div>
    );
  }

  return <Logs logs={logsQuery.data?.logs} loading={logsQuery.isLoading} />;
}

export interface LogsProps {
  logs: AnalysisLogsDtoType['logs'] | undefined;
  loading: boolean;
  fileName?: string;
}

function LogSkeleton({ count = 5 }: { count?: number }) {
  const skeletons = [];
  for (let i = 0; i < count; i++) {
    skeletons.push(i);
  }
  return (
    <>
      {skeletons.map((s) => (
        <div
          key={`logs-skel-${s}`}
          className="px-4 border-b border-gray-300 animate-pulse flex flex-col gap-1 py-2"
        >
          <p className="bg-gray-300 h-3 w-36 "></p>
          <p className="bg-gray-300 h-3 w-64 "></p>
          <p className="bg-gray-300 h-3 w-48 "></p>
        </div>
      ))}
    </>
  );
}

export function Logs(props: LogsProps) {
  const [showHyperLogs, setShowHyperLogs] = useState(false);
  const logsToShow: string[] = useMemo(() => {
    if (!props.logs) {
      return [];
    }
    if (showHyperLogs) {
      return props.logs;
    }

    return props.logs.filter((log) => !log.includes('| hyperscript'));
  }, [props.logs, showHyperLogs]);

  function download() {
    const element = document.createElement('a');
    const file = new Blob([logsToShow.join('\n')], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = props.fileName || 'logs.txt';
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  }
  return (
    <div className="flex flex-col gap-4 h-full overflow-scroll bg-white">
      <div className="flex items-center p-2 sticky top-0 bg-white z-10 border-b border-orange-2 justify-between">
        <label className="mr-2 flex items-center gap-2">
          Show Hyperscript Logs
          <Toggle
            on={showHyperLogs}
            onChange={(on) => {
              setShowHyperLogs(on);
            }}
          ></Toggle>
        </label>
        <button
          onClick={() => download()}
          className="flex items-center gap-1 px-2 py-1 bg-black hover:bg-gray-300 text-white"
        >
          <Download size={20} />
        </button>
      </div>
      {props.logs && props.logs.length === 0 && (
        <p>No logs available for this analysis yet.</p>
      )}
      {props.loading && <LogSkeleton count={10} />}
      {logsToShow.map((log, index) => (
        <div key={index} className="px-4 border-b border-gray-300">
          <p>{log}</p>
        </div>
      ))}
    </div>
  );
}
